import React, { useContext, useState, useRef } from 'react';
import useOutsideElement from 'components/Dashboard/utilities/useOutsideElement';
import { HexColorInput } from 'react-colorful';
import { store } from 'components/Store';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { Portal } from 'react-portal';
import ModalComponent from 'components/Common/Modal';
import parliamentarianUsers from 'components/Dashboard/SidebarElements/parliamentarianUsers';
import createNotification from 'components/Settings/Utilities/CreateNotification';
import { useAuth0 } from '@auth0/auth0-react';
import useTopicOrClientWord from 'components/Dashboard/hooks/useTopicOrClientWord';
import getAppSite, { useSiteSectionsNames } from 'utils/getAppSite';

const ReportDesignButton = (props) => {
  const {
    designProps,
    componentText = 'report',
    leftPosition,
    isOpenDesign,
    setIsOpenDesign,
    feed,
    chartButton,
    customColours,
    property,
  } = props;
  const { designState, setDesignState } = designProps ?? {};
  const changeState = ({ property, newValue }) => {
    let newState = { ...designState };
    newState[property] = newValue;
    setDesignState(newState);
  };

  const [isOpenPopup, setIsOpenPopup] = useState(false);
  const isOpen = isOpenDesign ?? isOpenPopup;
  const setIsOpen = setIsOpenDesign ?? setIsOpenPopup;
  const actionsElement = useRef(null);
  const { width, top, left, height } = actionsElement?.current?.getBoundingClientRect() ?? {};

  return (
    <div className='position-relative' ref={actionsElement}>
      <button
        className={`general-button rounded-pill border px-4 mr-3 hovered-button report-design-button bg-main-white`}
        onClick={() => setIsOpen(!isOpen)}
      >
        Design
      </button>
      {isOpen && (
        <Portal node={document && document.querySelector('body')}>
          <>
            <div
              className='general-close-background adjustment-of-position'
              onClick={() => {
                setIsOpen(false);
              }}
            />
            <div
              className='position-fixed rounded border bg-main-white report-design-options adjustment-of-position'
              style={{
                top: `${top + height + 5}px`,
                left: `${leftPosition ? left : left + width - 512}px`,
              }}
            >
              <div className='border-bottom flex-centered px-4 py-3'>
                <h3 className='mb-0 font-weight-bold title-h5'>Change the design of this {componentText}</h3>
              </div>
              <div className='px-4 py-3'>
                <ReportDesignPopup
                  designState={designState}
                  changeState={changeState}
                  componentText={componentText}
                  feed={feed}
                  chartButton={chartButton}
                  customColours={customColours}
                  property={property}
                />
              </div>
            </div>
          </>
        </Portal>
      )}
    </div>
  );
};

const ReportDesignPopup = (props) => {
  const globalState = useContext(store);
  const { state } = globalState;
  const { activePlan } = state;
  const { enableReportsRemoveBranding, subscriptionStatus, code } = activePlan;
  const { designState, changeState, componentText = 'report', feed, chartButton, customColours, property } = props;

  const { headerColour, backgroundColour, includeBranding, barColour } = designState ?? {};

  const tickBox = useRef(null);

  const trialAccount = subscriptionStatus === 'InTrial' || subscriptionStatus === 'TrialEnded';
  const enableRemoveBranding = (property ? activePlan[property] : enableReportsRemoveBranding) && !trialAccount;
  const { isAuthenticated } = useAuth0();
  const { newAgencyPlan } = useTopicOrClientWord();
  const { getName } = useSiteSectionsNames();

  return (
    <>
      <div className='d-lg-flex py-2 '>
        {!chartButton && (
          <div className='analytics-popup-basis pb-4 pb-lg-0'>
            <p className='font-weight-bold mb-2'>Header {getName({ property: 'Color' })?.toLowerCase()}</p>
            <ColorPicker
              customColours={customColours}
              color={headerColour}
              setColor={(color) => changeState({ property: 'headerColour', newValue: color })}
            />
          </div>
        )}
        {feed && (
          <div className='analytics-popup-basis pb-4 pb-lg-0'>
            <p className='font-weight-bold mb-2'>Bar {getName({ property: 'Color' })?.toLowerCase()}</p>
            <ColorPicker
              customColours={customColours}
              color={barColour}
              setColor={(color) => changeState({ property: 'barColour', newValue: color })}
            />
          </div>
        )}
        <div className={`analytics-popup-basis`}>
          <p className='font-weight-bold mb-2'>
            {feed ? 'Body' : 'Background'} {getName({ property: 'Color' })?.toLowerCase()}
          </p>
          <ColorPicker
            customColours={customColours}
            color={backgroundColour}
            setColor={(color) => changeState({ property: 'backgroundColour', newValue: color })}
          />
        </div>
      </div>
      {!parliamentarianUsers(code) && (
        <div className='py-4'>
          <div className='pl-1 pt-2'>
            <div
              className='checkbox-item d-flex my-0'
              ref={tickBox}
              data-tooltip-content={
                isAuthenticated
                  ? !enableRemoveBranding
                    ? `Upgrade to ${trialAccount ? 'a paid' : 'an'} ${newAgencyPlan && trialAccount ? '' : getAppSite() === 'usa' ? '' : 'Enterprise'} subscription to remove PolicyMogul branding`
                    : ''
                  : 'Create an account to remove PolicyMogul branding'
              }
              data-tooltip-event={!enableRemoveBranding ? 'click' : 'hover'}
              data-tooltip-id={'report-design-tooltip'}
            >
              <input
                id='pmBranding'
                type='checkbox'
                checked={includeBranding}
                onChange={() => {
                  createNotification(
                    'success',
                    `PolicyMogul branding ${includeBranding ? 'removed from' : 'included in'} this ${componentText}`,
                    'bottom-left'
                  );
                  changeState({ property: 'includeBranding', newValue: !includeBranding });
                }}
                disabled={!enableRemoveBranding}
              />
              <label htmlFor='pmBranding'>
                <p className='font-weight-bold mb-2'>
                  Include PolicyMogul branding {!enableRemoveBranding && <span className='icon-padlock ml-2' />}
                </p>
                <p className='light-50-text mb-0 paragraph-p2 font-weight-normal' style={{ lineHeight: '1.4' }}>
                  ‘Powered by PolicyMogul’ will {includeBranding ? '' : 'not '}appear at bottom of {componentText}
                </p>
              </label>
            </div>
          </div>
        </div>
      )}
      {/* <div className="rounded-bottom bg-light-blue-hovered px-4 border-top pb-3">
        <div className="pl-1 pt-1">
          <div className='checkbox-item d-flex mb-0'>
            <input 
              id='pm-defaults'
              type='checkbox' 
              checked={makeDefaultsSettings} 
              value={makeDefaultsSettings}
              onChange={()=>changeState({property: 'makeDefaultsSettings', newValue: !makeDefaultsSettings})} 
            />
            <label htmlFor="pm-defaults" style={{paddingTop: '1px'}}>
              <p className="mb-0">Make these my default settings for all reports</p>
            </label>
          </div>
        </div>
      </div> */}

      <ReactTooltip
        id={'report-design-tooltip'}
        effect={'solid'}
        type={'info'}
        className={'pricing-feature-tooltip feed-tooltip'}
        arrowColor={'transparent'}
        backgroundColor={'#00122B'}
        multiline={true}
      />
    </>
  );
};
0;
const ColorPicker = (props) => {
  const { color, setColor, customColours } = props;

  const [isOpen, setIsOpen] = useState(false);
  const hideOptions = () => {
    setIsOpen(false);
  };
  const actionsElement = useRef(null);
  useOutsideElement(actionsElement, hideOptions);
  return (
    <div className='flex-centered'>
      <div className='position-relative mr-1' ref={actionsElement}>
        <div
          className='rounded-circle border pointer'
          onClick={() => setIsOpen(!isOpen)}
          style={{ width: '27px', height: '27px', backgroundColor: color }}
        />
        {isOpen && (
          <div className='report-color-picker position-absolute z-10'>
            <div className='bg-main-white rounded border specific-top-padding-15 specific-bottom-padding-9 specific-left-padding-15 specific-right-padding-9'>
              <div className='d-flex flex-wrap'>
                {(customColours ?? defaultColours).map((item) => (
                  <button
                    key={item}
                    type='button'
                    className={`general-button specific-right-margin-6 specific-bottom-margin-6 rounded ${item === '#FFFFFF' ? 'border' : ''}`}
                    style={{ width: '30px', height: '30px', backgroundColor: item }}
                    onClick={() => setColor(item)}
                    aria-label={`Set color to ${item}`}
                  />
                ))}
                <div className='d-flex hex-color-input specific-right-margin-6'>
                  <span className='text-main-blue-35 d-inline-block d-flex align-items-center justify-content-center'>
                    <span>#</span>
                  </span>
                  <HexColorInput
                    color={color}
                    onChange={setColor}
                    alpha
                    prefixed
                    className='border rounded px-2 specific-y-padding-3 w-100'
                    style={{ fontSize: '14px' }}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      <p className='m-0 ml-2 pointer' onClick={() => setIsOpen(!isOpen)}>
        {formatColor(color).toUpperCase()}
      </p>
    </div>
  );
};

const hex_is_light = (color = '#FFFFFF') => {
  const hex = color.replace('#', '');
  const c_r = parseInt(hex.substring(0, 0 + 2), 16);
  const c_g = parseInt(hex.substring(2, 2 + 2), 16);
  const c_b = parseInt(hex.substring(4, 4 + 2), 16);
  const brightness = (c_r * 299 + c_g * 587 + c_b * 114) / 1000;
  return brightness > 155;
};

const DesignPopupInsideMoreButton = (props) => {
  const { designProps, isOpen, setIsOpen } = props;
  const { setDesignState, designState } = designProps;
  const changeState = ({ property, newValue }) => {
    let newState = { ...designState };
    newState[property] = newValue;
    setDesignState(newState);
  };

  return (
    <ModalComponent
      setIsOpen={(value) => {
        setIsOpen(value);
      }}
      overlayClassName='react-modal-overlay report-extra-index-modal'
      isOpen={isOpen}
      maxWidth={'496'}
    >
      <div className='border-bottom flex-centered px-4 py-3'>
        <h3 className='mb-0 font-weight-bold title-h5'>Change the design of this report</h3>
      </div>
      <div className='px-4 py-3'>
        <ReportDesignPopup designState={designState} changeState={changeState} isFromPopup={true} />
      </div>
    </ModalComponent>
  );
};

const formatColor = (color) => {
  return (color?.length > 7 ? color?.slice(0, -2) : color).toUpperCase();
};

const defaultColours = [
  '#FBDBC5',
  '#FFE9B5',
  '#E2F1EB',
  '#D2E9E0',
  '#DFF1FB',
  '#B1DFF6',
  '#E4E9ED',
  '#F7C2D5',
  '#FBDDE5',
  '#FFFFFF',
];

export { hex_is_light, DesignPopupInsideMoreButton, formatColor, ReportDesignPopup };
export default ReportDesignButton;
